import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  XMarkIcon,
  EnvelopeIcon,
  MapPinIcon,
  UsersIcon,
  CalendarIcon,
  LinkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { adminManagementAPI } from '../services/api';
import { EmployerOverview, PaginationInfo } from '../types';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateUtils';

interface DetailedEmployer extends EmployerOverview {
  companyDescription?: string;
  industry?: string;
  website?: string;
  location?: string;
  companySize?: string;
}

const EmployerManagement: React.FC = () => {
  const [employers, setEmployers] = useState<EmployerOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState<DetailedEmployer | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });

  useEffect(() => {
    loadEmployers();
  }, [pagination.currentPage]);

  const loadEmployers = async () => {
    try {
      setLoading(true);
      const response = await adminManagementAPI.getEmployers({
        page: pagination.currentPage,
        limit: 10,
        search: searchTerm || undefined
      });
      setEmployers(response.data.employers || []);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadEmployers();
  };

  const handleViewEmployer = async (employerId: string) => {
    setViewLoading(true);
    setShowViewModal(true);
    try {
      const response = await adminManagementAPI.getEmployerById(employerId);
      setSelectedEmployer(response.data);
    } catch (error) {
      toast.error('Failed to load employer details');
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Employer Management</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Manage employer accounts and company profiles
        </p>
      </div>

      {/* Search */}
      <div className="card-admin p-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by company name or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-admin pl-10"
              />
            </div>
          </div>
          <button type="submit" className="btn-admin">Search</button>
        </form>
      </div>

      {/* Employers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-600"></div>
          </div>
        ) : employers.length === 0 ? (
          <div className="col-span-2 text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No employers found.</p>
          </div>
        ) : (
          employers.map((employer) => (
            <div key={employer.id} className="card-admin p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <BuildingOfficeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {employer.companyName}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {employer.firstName} {employer.lastName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">{employer.email}</p>
                  </div>
                </div>
                {employer.isActive ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    <XCircleIcon className="w-3 h-3 mr-1" />
                    Inactive
                  </span>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Contact:</span>
                  <span className="ml-2">{employer.firstName} {employer.lastName}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Joined:</span>
                  <span className="ml-2">{formatDate(employer.createdAt)}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <button 
                    className="w-full btn-admin text-sm flex items-center justify-center"
                    onClick={() => handleViewEmployer(employer.id)}
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    View Profile
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && employers.length > 0 && (
        <div className="card-admin p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={!pagination.hasPrev}
                className="btn-admin-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={!pagination.hasNext}
                className="btn-admin-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Employer Modal */}
      {showViewModal && selectedEmployer && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="modal-overlay" onClick={() => setShowViewModal(false)}></div>
          <div className="modal-container max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="modal-header bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <BuildingOfficeIcon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{selectedEmployer.companyName}</h2>
                  <p className="text-blue-100 opacity-90">Company Profile</p>
                </div>
                <div className="flex items-center space-x-3">
                  {selectedEmployer.isActive ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-100 border border-green-400/30">
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-500/20 text-red-100 border border-red-400/30">
                      <XCircleIcon className="w-4 h-4 mr-1" />
                      Inactive
                    </span>
                  )}
                  <button 
                    onClick={() => setShowViewModal(false)} 
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-content">
              {viewLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading employer details...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  
                  {/* Quick Info Cards */}
                  <div className="grid md:grid-cols-2 gap-6">
                    
                    {/* Contact Information Card */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mr-3">
                          <EnvelopeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Contact Person</p>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedEmployer.firstName} {selectedEmployer.lastName}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Email Address</p>
                          <a 
                            href={`mailto:${selectedEmployer.email}`} 
                            className="text-base text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                          >
                            <EnvelopeIcon className="w-4 h-4 mr-1" />
                            {selectedEmployer.email}
                          </a>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Member Since</p>
                          <p className="text-base text-gray-900 dark:text-white flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(selectedEmployer.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Company Quick Stats */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mr-3">
                          <BuildingOfficeIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Overview</h3>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Industry</p>
                          <p className="text-base font-medium text-gray-900 dark:text-white">
                            {selectedEmployer.industry || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Company Size</p>
                          <p className="text-base text-gray-900 dark:text-white flex items-center">
                            <UsersIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {selectedEmployer.companySize || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                          <p className="text-base text-gray-900 dark:text-white flex items-center">
                            <MapPinIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {selectedEmployer.location || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Company Description */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mr-3">
                        <InformationCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Description</h3>
                    </div>
                    <div className="prose prose-gray dark:prose-invert max-w-none">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {selectedEmployer.companyDescription || 'No company description provided yet.'}
                      </p>
                    </div>
                  </div>

                  {/* Website & Links */}
                  {selectedEmployer.website && (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center mr-3">
                          <LinkIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Website</h3>
                      </div>
                      <a 
                        href={selectedEmployer.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <GlobeAltIcon className="w-5 h-5 mr-2" />
                        Visit Website
                        <LinkIcon className="w-4 h-4 ml-2" />
                      </a>
                    </div>
                  )}

                  {/* Admin Actions */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Admin Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      <button className="btn-admin-secondary">
                        <EnvelopeIcon className="w-4 h-4 mr-2" />
                        Send Email
                      </button>
                      <button className="btn-admin-secondary">
                        <EyeIcon className="w-4 h-4 mr-2" />
                        View Activity
                      </button>
                      <button className={`btn-admin-secondary ${selectedEmployer.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}>
                        {selectedEmployer.isActive ? (
                          <>
                            <XCircleIcon className="w-4 h-4 mr-2" />
                            Deactivate Account
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="w-4 h-4 mr-2" />
                            Activate Account
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-admin-secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerManagement;